import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sparkles, Home, Calendar, CheckCircle2, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { api, formatError, addonsFor } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { findCity, RIVIERA_FREQUENCIES } from "@/lib/riviera";
import { MonacoBadge, EYEBROW_AZ } from "@/components/riviera/RivieraUI";
import { StepService, StepProperty, StepSchedule, StepReview, serviceKey, supportsExtras } from "@/pages/riviera/InquirySteps";

const STEPS = [
  { key: "service", icon: Sparkles }, { key: "property", icon: Home },
  { key: "schedule", icon: Calendar }, { key: "review", icon: CheckCircle2 },
];

export default function RivieraInquiry() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState(() => ({
    service_type: "Deep Cleaning", frequency: "one-time", addons: [],
    sqm: "", floors: 1, bedrooms: 1, bathrooms: 1, client_type: "Residential / Private",
    city_slug: findCity(params.get("city"))?.slug || "nice", date: "", start_time: "10:00",
    customer_name: user?.name || "", customer_email: user?.email || "", customer_phone: "",
    address: "", postal_code: "", access_instructions: "", special_requests: "", consent: false,
  }));
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const city = findCity(form.city_slug);

  useEffect(() => {
    const ok = supportsExtras(form.service_type) ? addonsFor(form.service_type).map((x) => x.key) : [];
    if (form.addons.some((k) => !ok.includes(k))) upd("addons", form.addons.filter((k) => ok.includes(k)));
  }, [form.service_type, form.addons]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const canNext = [
    !!form.service_type,
    Number(form.sqm) > 0,
    form.city_slug && form.customer_name.trim() && /\S+@\S+\.\S+/.test(form.customer_email) && form.customer_phone.trim() && form.address.trim(),
    form.consent,
  ][step];

  const freqLabel = t(RIVIERA_FREQUENCIES.find((f) => f.value === form.frequency)?.labelKey || "");
  const rows = [
    [t("riviera.inquiry.summary.service"), t(`riviera.services_list.${serviceKey(form.service_type)}`)],
    [t("riviera.inquiry.summary.frequency"), freqLabel],
    [t("riviera.inquiry.summary.property"), form.sqm ? `${form.sqm} m² · ${form.bedrooms} / ${form.bathrooms} · ${form.floors}` : ""],
    [t("riviera.inquiry.summary.extras"), form.addons.map((k) => t(`book_v2.extra_labels.${k}`, k)).join(", ")],
    [t("riviera.inquiry.summary.city"), city?.name],
    [t("riviera.inquiry.summary.date"), form.date ? `${form.date} · ${form.start_time}` : ""],
    [t("riviera.inquiry.summary.contact"), [form.customer_name, form.customer_email, form.customer_phone].filter(Boolean).join(" · ")],
  ];

  const submit = async () => {
    if (!form.consent) { toast.error(t("riviera.inquiry.toast_consent")); return; }
    setSending(true);
    try {
      const r = await api.post("/public/leads", {
        customer_name: form.customer_name.trim(), email: form.customer_email.trim().toLowerCase(), phone: form.customer_phone.trim(),
        address: form.address.trim(), postal_code: form.postal_code.trim(), city: city.name,
        preferred_date: form.date || null, preferred_time: form.date ? form.start_time : null,
        service_type: form.service_type, frequency: form.frequency, addons: form.addons,
        property_size: String(form.sqm), floors: parseInt(form.floors) || 1,
        bedrooms: parseInt(form.bedrooms) || 0, bathrooms: parseInt(form.bathrooms) || 0, client_type: form.client_type,
        access_instructions: form.access_instructions, special_requests: form.special_requests,
        preferred_contact: "email", country: "FR", source: "riviera_inquiry",
      });
      setDone({ id: r.data.id, monaco: !!city.inquiryOnly });
      toast.success(t("riviera.inquiry.toast_ok"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setSending(false); }
  };

  if (done) return <Confirmation t={t} done={done} />;

  return (
    <div className="min-h-screen bg-[#F0F9FF]/60 py-10 md:py-16" data-testid="riviera-inquiry">
      <div className="max-w-6xl mx-auto px-4">
        <p className={EYEBROW_AZ}>{t("riviera.inquiry.eyebrow")}</p>
        <h1 className="text-4xl sm:text-5xl text-[#0F172A] mt-2">{t("riviera.inquiry.title")}</h1>
        <p className="mt-3 text-slate-600 max-w-2xl">{t("riviera.inquiry.subtitle")}</p>
        {city?.inquiryOnly && <div className="mt-4"><MonacoBadge t={t} testid="inquiry-monaco-badge" /></div>}
        <div className="mt-8 grid lg:grid-cols-[1fr_340px] gap-6">
          <div className="min-w-0 bg-white rounded-3xl ring-1 ring-slate-200 p-6 md:p-8 shadow-[0_4px_20px_-2px_rgba(27,58,107,0.06)]">
            <StepBar t={t} step={step} />
            {step === 0 && <StepService t={t} form={form} upd={upd} />}
            {step === 1 && <StepProperty t={t} form={form} upd={upd} />}
            {step === 2 && <StepSchedule t={t} form={form} upd={upd} />}
            {step === 3 && <StepReview t={t} form={form} upd={upd} rows={rows} />}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
              <button type="button" onClick={() => setStep((s) => s - 1)} disabled={step === 0} data-testid="fr-prev"
                className="inline-flex items-center gap-1 px-5 h-11 rounded-full text-sm font-semibold ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                <ArrowLeft className="w-4 h-4" /> {t("riviera.inquiry.back")}
              </button>
              {step < 3 ? (
                <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="fr-next"
                  className="inline-flex items-center gap-1 px-6 h-11 rounded-full text-sm font-semibold text-white bg-[#1B3A6B] hover:bg-[#102547] disabled:opacity-40 transition-colors">
                  {t("riviera.inquiry.next")} <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={sending || !form.consent} data-testid="fr-submit"
                  className="inline-flex items-center px-7 h-11 rounded-full text-sm font-semibold text-white bg-[#0EA5E9] hover:bg-[#0284C7] disabled:opacity-40 transition-colors">
                  {sending ? t("riviera.inquiry.submitting") : t("riviera.inquiry.submit")}
                </button>
              )}
            </div>
          </div>
          <SideBox t={t} rows={rows.slice(0, 5)} />
        </div>
      </div>
    </div>
  );
}

function StepBar({ t, step }) {
  return (
    <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-1" data-testid="fr-steps">
      {STEPS.map((s, i) => {
        const Icon = i < step ? Check : s.icon;
        return (
          <div key={s.key} className="flex items-center gap-1.5 shrink-0">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${i === step ? "bg-[#1B3A6B] text-white" : i < step ? "bg-[#0EA5E9] text-white" : "bg-slate-100 text-slate-400"}`}><Icon className="w-4 h-4" /></div>
            <span className={`text-xs font-semibold ${i === step ? "text-[#1B3A6B]" : "text-slate-500"}`}>{t(`riviera.inquiry.steps.${s.key}`)}</span>
            {i < STEPS.length - 1 && <div className="w-5 h-px bg-slate-200" />}
          </div>
        );
      })}
    </div>
  );
}

function SideBox({ t, rows }) {
  return (
    <aside className="lg:sticky lg:top-24 h-fit bg-[#1B3A6B] text-white rounded-3xl p-6" data-testid="fr-request-summary">
      <p className="text-xs uppercase tracking-[0.18em] font-bold text-sky-300">{t("riviera.inquiry.side_eyebrow")}</p>
      <h3 className="font-display text-2xl mt-1">{t("riviera.inquiry.side_title")}</h3>
      <p className="text-sm text-slate-300 mt-2">{t("riviera.inquiry.side_body")}</p>
      <div className="mt-4 border-t border-white/10 pt-4 space-y-1.5 text-sm">
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3"><span className="text-slate-300">{k}</span><span className="text-right">{v}</span></div>
        ))}
      </div>
      <ul className="mt-5 space-y-2 text-sm">
        {(t("riviera.inquiry.side_points", { returnObjects: true }) || []).map((p) => (
          <li key={p} className="flex gap-2 items-center"><Check className="w-4 h-4 text-sky-300" /> {p}</li>
        ))}
      </ul>
    </aside>
  );
}

function Confirmation({ t, done }) {
  return (
    <div className="min-h-[70vh] bg-[#F0F9FF]/60 py-16 px-4" data-testid="fr-confirmation">
      <div className="max-w-lg mx-auto bg-white rounded-3xl ring-1 ring-slate-200 p-8 text-center tl-reveal">
        <div className="w-14 h-14 mx-auto rounded-full bg-sky-50 text-[#0EA5E9] flex items-center justify-center"><CheckCircle2 className="w-7 h-7" /></div>
        <h1 className="text-3xl text-[#0F172A] mt-4">{t("riviera.inquiry.conf_title")}</h1>
        <p className="text-sm text-slate-600 mt-2">{t("riviera.inquiry.conf_body")}</p>
        {done.monaco && <p className="text-xs text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-xl p-3 mt-4" data-testid="fr-confirmation-monaco">{t("riviera.inquiry.conf_monaco")}</p>}
        <p className="mt-5 text-sm"><span className="text-slate-500">{t("riviera.inquiry.conf_ref")}: </span><b data-testid="fr-confirmation-ref">{done.id.slice(0, 8).toUpperCase()}</b></p>
        <Link to="/fr" className="mt-6 inline-flex items-center justify-center w-full h-11 rounded-full text-sm font-semibold text-white bg-[#1B3A6B] hover:bg-[#102547]" data-testid="fr-confirmation-back">{t("riviera.inquiry.conf_back")}</Link>
      </div>
    </div>
  );
}
